import FitCurveIcon from '@/assets/images/FitCurveIcon.svg';
import FitVarsityIcon from '@/assets/images/FitVarsityIcon.svg';
import BackgroundImage from '@/assets/images/session-detail-bg.svg';
import Timer from '@/assets/images/Timer.svg';
import CustomerDetail from '@/components/common/Customer/CustomerDetail';
import HeaderWrapper from '@/components/common/Header/HeaderWrapper';
import StatusBadge from '@/components/common/StatusBadge/StatusBadge';
import TrainerDetail from '@/components/common/Trainer/TrainerDetail';
import { CustomDialog } from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { GET_USER_BY_ID } from '@/Graphql/queries';
import useCancelSession from '@/hooks/useCancelSession';
import { Role, Session, SessionStatus } from '@/Types/gql/graphql';
import { authState } from '@/vars/auth';
import { useLazyQuery, useQuery } from '@apollo/client';
import dayjs from 'dayjs';
import { ArrowLeft, Calendar, Clock, MoreVertical } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import ScheduleSession from '../sessions/components/ScheduleSession';
import { GET_SESSION_BALANCE } from '../sessions/GraphQl/queries';
import { GET_SESSION_BY_ID } from './GraphQl/queries';
const SessionDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const { userDetails } = authState();
  const [timeRemaining, setTimeRemaining] = useState(0);
  // changed to 12hour
  const [isMoreThan24Hours, setIsMoreThan24Hours] = useState(false);
  const [trainerAssigned, setTrainerAssigned] = useState(false);
  const [cancelDialog, setCancelDialog] = useState(false);
  const [open, setOpen] = useState(false);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const [isCombinedLoading, setIsCombinedLoading] = useState(true);

  const [getUserById, { data: trainerData, loading: trainerLoading }] = useLazyQuery(
    GET_USER_BY_ID,
    { fetchPolicy: 'network-only', notifyOnNetworkStatusChange: true },
  );

  const { data: sessionBalance } = useQuery(GET_SESSION_BALANCE, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  const [getCustomerById, { data: customerData, loading: customerLoading }] = useLazyQuery(
    GET_USER_BY_ID,
    { fetchPolicy: 'network-only', notifyOnNetworkStatusChange: true },
  );

  const { data, loading } = useQuery(GET_SESSION_BY_ID, {
    variables: {
      id: id || '',
    },
    skip: !id,
    onCompleted: (res) => {
      const sessionStartTime = new Date(res.getSessionById?.sessionStartTime);
      const currentTime = new Date();
      getUserById({ variables: { id: res.getSessionById?.trainerId as string } });
      getCustomerById({ variables: { id: res.getSessionById?.traineeId as string } });
      const timeDifferenceInSeconds = Math.max(
        Math.floor((sessionStartTime.getTime() - currentTime.getTime()) / 1000),
        0,
      );
      if (timeDifferenceInSeconds > 12 * 60 * 60) {
        setIsMoreThan24Hours(true);
      } else {
        setIsMoreThan24Hours(false);
      }
      setTimeRemaining(timeDifferenceInSeconds);
      if (res.getSessionById?.trainerId) {
        setTrainerAssigned(true);
      }
    },
  });
  const session = data?.getSessionById;
  console.log('session', session);
  const isPastSession = session?.sessionEndTime && session.sessionEndTime < new Date();

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeRemaining((prevTime) => {
        if (prevTime <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const formatTime = (time: number) => {
    const hours = Math.floor(time / 3600);
    const minutes = Math.floor((time % 3600) / 60);
    const seconds = time % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };
  const { handleCancelSession } = useCancelSession();

  const handleEditClick = () => {
    setOpen(true);
  };

  const startingSoon =
    timeRemaining < 5 * 60 &&
    session?.sessionEndTime &&
    session.sessionStatus !== 'Completed' &&
    session.sessionStatus !== 'Cancelled' &&
    new Date(session.sessionEndTime) > new Date();

  const handleGoBack = () => {
    if (location.key === 'default') {
      navigate('/');
    } else {
      navigate(-1);
    }
  };

  useEffect(() => {
    // If either is loading, set to true
    if (loading || customerLoading || trainerLoading) {
      setIsCombinedLoading(true);
    } else {
      // Introduce a slight delay to cover the transition edge case
      const timeout = setTimeout(() => {
        setIsCombinedLoading(false);
      }, 200); // adjust the delay as needed

      return () => clearTimeout(timeout);
    }
  }, [loading, customerLoading, trainerLoading]);

  return (
    <div className='h-screen bg-[#0A051E] text-white overflow-auto'>
      {/* Header */}
      <HeaderWrapper>
        <div className='flex items-center gap-2'>
          <Button
            onClick={() => handleGoBack()}
            id='session-details-back-btn'
            variant='ghost'
            size='icon'
            className='text-white'
          >
            <ArrowLeft className='h-5 w-5' />
          </Button>
          <h1 className='text-lg font-semibold'>{session?.sessionUniqId}</h1>
        </div>
        <div className='flex items-center gap-4'>
          {timeRemaining >= 12 * 60 * 60 ? (
            <StatusBadge
              status={
                isPastSession && session?.sessionStatus != 'Scheduled'
                  ? 'Canceled'
                  : session?.sessionStatus === SessionStatus.Open
                    ? 'Scheduled'
                    : session?.sessionStatus
              }
            />
          ) : timeRemaining < 12 * 60 * 60 && timeRemaining > 0 ? (
            <div className='flex flex-row gap-2 items-center'>
              <img src={Timer} alt='timer' />
              <div className='text-base text-white font-normal'>Starts in</div>
              <div className='text-base text-white font-semibold'>{formatTime(timeRemaining)}</div>
            </div>
          ) : (
            <StatusBadge
              status={
                session?.sessionStatus == 'Completed'
                  ? 'Completed'
                  : session?.sessionStatus == 'Cancelled'
                    ? 'Canceled'
                    : session?.sessionStatus == SessionStatus.Noshow
                      ? 'No-Show'
                      : 'Scheduled'
              }
            />
          )}
          <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
            <PopoverTrigger asChild>
              {timeRemaining >= 24 * 60 * 60 && (
                <Button
                  id='session-detail-more-options'
                  variant='ghost'
                  size='icon'
                  className='text-white'
                >
                  <MoreVertical className='h-5 w-5' />
                </Button>
              )}
            </PopoverTrigger>
            <PopoverContent align='center' className='p-2 w-40 border-none bg-gray-800'>
              <ul className='text-sm text-white rounded-sm'>
                <li
                  className='p-2 hover:bg-zinc-900 cursor-pointer rounded-sm'
                  onClick={() => {
                    handleEditClick();
                    setIsPopoverOpen(false);
                  }}
                >
                  Edit
                </li>
              </ul>
            </PopoverContent>
          </Popover>
        </div>
      </HeaderWrapper>

      {/* Session Card */}
      <div className='w-[1060px] max-w-[95%] mt-10 mx-auto p-4'>
        {loading ? (
          <Skeleton className='h-40 w-full rounded-md' />
        ) : (
          <Card
            className='border-none mt-4 m'
            style={{
              background: startingSoon
                ? 'linear-gradient(154.36deg, rgba(47, 47, 47, 0.44) 11.3%, rgba(47, 47, 47, 0.44) 95.38%),linear-gradient(173.47deg, rgba(211, 138, 69, 0) 32.21%, rgba(211, 185, 69, 0.1) 94.86%)'
                : 'linear-gradient(154.36deg, rgba(47, 47, 47, 0.44) 11.3%, rgba(47, 47, 47, 0.44) 95.38%)',
            }}
          >
            <div
              className='p-[1px] rounded-xl'
              style={{
                background: startingSoon
                  ? 'linear-gradient(179.55deg, rgba(240, 231, 113, 0) 15.61%, rgba(238, 211, 96, 0.521698) 62.24%, rgba(255, 164, 76, 0.34) 99.61%)'
                  : '',
              }}
            >
              <div
                className='flex p-6 gap-4 rounded-xl items-center'
                style={{
                  background: startingSoon
                    ? 'linear-gradient(to top, #151525, #272428)'
                    : 'rgb(25, 27, 47)',
                }}
              >
                <div className='flex flex-col md:flex-row items-center w-full justify-between gap-4'>
                  <div className='flex items-center gap-4'>
                    {timeRemaining >= 12 * 60 * 60 && (
                      <div className='flex flex-col items-center justify-center w-[60px] h-[60px] bg-[#00000040] rounded-full'>
                        <div className='text-sm text-textSecondary leading-4'>
                          {dayjs(session?.sessionStartTime).format('MMM')}
                        </div>
                        <div className='text-2xl font-bold text-white leading-7'>
                          {dayjs(session?.sessionStartTime).format('DD')}
                        </div>
                      </div>
                    )}
                    <div className='bg-white/10 p-2 rounded-full w-[60px] h-[60px] flex items-center justify-center'>
                      <img
                        className='w-[40px] h-[40px]'
                        src={
                          session?.trainingType?.icon
                            ? session?.trainingType?.icon
                            : session?.programDetails?.programName === 'FitVarsity'
                              ? FitVarsityIcon
                              : session?.programDetails?.programName === 'FitCurve'
                                ? FitCurveIcon
                                : ''
                        }
                        alt={session?.trainingType?.title}
                      />
                    </div>
                  </div>
                  <div className='flex-1'>
                    <h2 className='text-base font-medium text-white'>
                      {timeRemaining <= 12 * 60 * 60 && trainerAssigned && !trainerLoading
                        ? `${session?.trainingType ? session?.programDetails?.programName + `(${session?.trainingType?.title})` : session?.programDetails?.programName} with ${userDetails?.role === Role.Trainer || userDetails?.role === Role.Coach ? customerData?.getUserById?.firstName : trainerData?.getUserById?.firstName}`
                        : `${session?.trainingType ? session?.programDetails?.programName + `(${session?.trainingType?.title})` : session?.programDetails?.programName}`}
                    </h2>
                    <div className='flex items-center gap-2 text-base font-medium text-white mt-2'>
                      <Calendar className='w-5 h-5' />
                      {dayjs(session?.sessionStartTime).format('ddd MM/DD/YYYY')}
                      <Separator className='w-[1px] h-3.5 bg-[#6F7789]' />
                      <Clock className='w-5 h-5' />
                      {dayjs(session?.sessionStartTime).format('h:mm')} -{' '}
                      {dayjs(session?.sessionEndTime).format('h:mm A')}
                    </div>
                  </div>
                  <div className='flex gap-1 text-white'>
                    {timeRemaining > 0 &&
                    timeRemaining >= 5 * 60 &&
                    session?.sessionStatus !== 'Completed' &&
                    session?.sessionStatus !== 'Cancelled' ? (
                      <>
                        <img src={Timer} alt='timer' />
                        <div className='text-base text-white font-normal'>Starts in</div>
                        <div className='text-base text-white font-semibold'>
                          {formatTime(timeRemaining)}
                        </div>
                      </>
                    ) : session?.sessionStatus === 'Completed' ? (
                      <div className='flex flex-row gap-3 items-center'>
                        {session?.sessionStatus == SessionStatus.Completed &&
                          ((userDetails?.role === Role.Trainee &&
                            session.trainerExpertiseRating == null) ||
                            ((userDetails?.role === Role.Trainer ||
                              userDetails?.role === Role.Coach) &&
                              session.assesment?.assessment == null)) && (
                            <div className='flex items-center gap-2 mt-1'>
                              <Link
                                id='add-review'
                                to={`/feedback/${session?._id}`}
                                className='flex items-center text-sm px-3 py-0 text-white bg-bgSecondary rounded-full h-8'
                              >
                                + Add Review
                              </Link>
                            </div>
                          )}
                        <StatusBadge status='Completed' />
                      </div>
                    ) : session?.sessionStatus === 'Cancelled' ? (
                      <StatusBadge status='Canceled' />
                    ) : session?.sessionStatus === SessionStatus.Noshow &&
                      new Date(session?.sessionEndTime) < new Date() ? (
                      <StatusBadge status='No-Show' />
                    ) : (
                      <div className='flex flex-col gap-3'>
                        <div className='text-center text-base font-semibold text-white'>
                          {timeRemaining ? `Starts in ${formatTime(timeRemaining)}` : 'In Progress'}
                        </div>
                        {(userDetails?.role === Role.Trainer || userDetails?.role === Role.Coach) &&
                          session?.traineeIdUser && (
                            <Button
                              id='join-btn'
                              className='text-black hover:bg-gray-200'
                              size='sm'
                              onClick={() => navigate(`/meeting/${session?._id}`)}
                            >
                              Join
                            </Button>
                          )}
                        {userDetails?.role === Role.Trainee && session?.trainerIdUser && (
                          <Button
                            id='join-btn'
                            className='text-black hover:bg-gray-200'
                            size='sm'
                            onClick={() => navigate(`/meeting/${session?._id}`)}
                          >
                            Join
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </Card>
        )}
        {/* Center Content */}
        {trainerLoading ? (
          <Skeleton className='h-40 w-full rounded-md mt-4' />
        ) : timeRemaining <= 12 * 60 * 60 &&
          trainerAssigned &&
          userDetails?.role === Role.Trainee ? (
          <div className='text-center mt-5 flex flex-col items-start'>
            <span className='font-semibold text-base '>Meet Your Trainer</span>
            <TrainerDetail trainerData={trainerData || null} loading={isCombinedLoading} />
          </div>
        ) : userDetails?.role === Role.Coach || userDetails?.role === Role.Trainer ? (
          <div className='text-center mt-5 flex flex-col items-start'>
            <span className='font-semibold text-base '>Meet Your Customer</span>
            <CustomerDetail
              customerData={customerData || null}
              loading={isCombinedLoading}
              customerId={session?.traineeId || ''}
              sessionStartTime={session?.sessionStartTime || null}
            />
          </div>
        ) : (
          !loading && (
            <div className='text-center my-8'>
              <div className='flex items-center justify-center w-full mb-8'>
                <img src={BackgroundImage} alt='session detail background' />
              </div>
              <h2 className='text-xl font-semibold mb-8'>Your session has been Scheduled.</h2>
              {isMoreThan24Hours && (
                <Button
                  variant='secondary'
                  id='cancel-session'
                  className='bg-gray-800 hover:bg-gray-700 text-white border-gray-700 rounded-xl'
                  onClick={() => setCancelDialog(true)}
                >
                  Cancel Session
                </Button>
              )}
            </div>
          )
        )}
      </div>
      {open && (
        <ScheduleSession
          open={open}
          setOpen={setOpen}
          sessionData={session as Session}
          freeSessionBalance={sessionBalance?.getCurrentUser?.freeSessionBalance ?? undefined}
          fitPrimeBalance={sessionBalance?.getCurrentUser?.fitPrimeBalance ?? undefined}
          fitCurveBalance={sessionBalance?.getCurrentUser?.fitCurveBalance ?? undefined}
          fitVarsityBalance={sessionBalance?.getCurrentUser?.fitVarsityBalance ?? undefined}
        />
      )}
      <CustomDialog
        open={cancelDialog}
        desc='Are you sure you want to cancel session?'
        onCancel={() => setCancelDialog(false)}
        title='Cancel Session'
        onConfirm={() =>
          handleCancelSession(id || '', session?.programId || '', session?.sportId || '')
        }
      />
    </div>
  );
};

export default SessionDetails;
